import React, { useEffect, useState } from 'react';
import { Check, X, Inbox, Loader } from 'lucide-react';
import organizationsApi from '../../utils/organizationsApi';

const MUTED = '#64748B';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Owner/admin incoming join-request review queue.
 *
 * The fetch effect is keyed by [organizationId, refreshKey] ONLY (never by
 * inline callback props) per CLAUDE.md effect-safety: a decision bumps
 * refreshKey to refetch rather than pushing a callback-driven refresh loop.
 * A 404 (caller is not an approver) degrades to a graceful empty state.
 */
function JoinRequestQueue({ organizationId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [deciding, setDeciding] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    organizationsApi
      .listJoinRequests({ scope: 'incoming', organizationId })
      .then((data) => {
        if (cancelled) return;
        setForbidden(false);
        setRequests(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // Not an approver for this org — degrade silently.
          setForbidden(true);
          setRequests([]);
        } else {
          setError('Could not load join requests.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, refreshKey]);

  async function decide(id, decision) {
    setDeciding(id);
    setError('');
    try {
      await organizationsApi.decideJoinRequest(id, decision);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError('Could not update that request. Please try again.');
    } finally {
      setDeciding(null);
    }
  }

  // Non-approvers see nothing (the backend 404s their incoming queue).
  if (forbidden) return null;

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: '#000' }}>Join requests</h2>
      <p style={{ fontSize: 14, color: MUTED, margin: '0 0 20px', lineHeight: 1.5 }}>
        People who requested access to this organization. Approve to add them as active members.
      </p>

      {error && (
        <div
          style={{
            border: '1px solid #FF0004',
            background: '#FFF5F5',
            color: '#000',
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: MUTED, fontSize: 14 }}>
          <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading requests…
        </div>
      ) : requests.length === 0 ? (
        <div
          style={{
            border: '1px dashed #A0ABBB',
            padding: '28px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            color: MUTED,
            textAlign: 'center',
          }}
        >
          <Inbox size={22} />
          <span style={{ fontSize: 14 }}>No pending join requests.</span>
        </div>
      ) : (
        <div style={{ border: '1px solid #000' }}>
          {requests.map((r, i) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '14px 16px',
                borderTop: i ? '0.4px solid #D5DAE1' : 'none',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: '#000', lineHeight: 1.4 }}>
                  {r.reason ? r.reason : <span style={{ color: MUTED, fontStyle: 'italic' }}>No reason provided</span>}
                </div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>Requested {formatWhen(r.created_at)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => decide(r.id, 'approve')}
                  disabled={deciding === r.id}
                  aria-label="Approve request"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    background: '#000',
                    color: '#fff',
                    border: '1px solid #000',
                    padding: '8px 14px',
                    cursor: deciding === r.id ? 'not-allowed' : 'pointer',
                    opacity: deciding === r.id ? 0.4 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  <Check size={15} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => decide(r.id, 'reject')}
                  disabled={deciding === r.id}
                  aria-label="Reject request"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    background: '#fff',
                    color: '#000',
                    border: '1px solid #000',
                    padding: '8px 14px',
                    cursor: deciding === r.id ? 'not-allowed' : 'pointer',
                    opacity: deciding === r.id ? 0.4 : 1,
                    fontFamily: 'inherit',
                  }}
                >
                  <X size={15} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default JoinRequestQueue;
